'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import Image from 'next/image';
import styles from './FeaturesCarousel.module.css';

interface ContentBlock {
  title: string;
  content: string;
  image?: string;
}

interface FeaturesCarouselProps {
  translations: {
    badge?: string;
    sectionTitle?: string;
    sectionIntro?: string;
    contentBlocks?: ContentBlock[];
    readMore?: string;
  };
  altTexts?: {
    kiosk?: Record<string, string>;
    kioskFeatures?: Record<string, string>;
  };
  imageDirectory?: string;
}

export default function FeaturesCarousel({ translations, altTexts, imageDirectory = 'kiosk-features' }: FeaturesCarouselProps) {
  const [selectedFeature, setSelectedFeature] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLElement | null)[]>([]);

  const contentBlocks = translations.contentBlocks || [];
  const totalSlides = contentBlocks.length;

  // Close modal
  const closeModal = useCallback(() => {
    setSelectedFeature(null);
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }, []);

  // Open modal
  const openModal = useCallback((index: number) => {
    setSelectedFeature(index);
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }, []);

  // Restore overflow on unmount (e.g. navigation while modal is open)
  useEffect(() => {
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    
    if (selectedFeature !== null) {
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [selectedFeature, closeModal]);

  // Navigation handlers - SIMPLE
  const scrollToSlide = useCallback((index: number) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const card = cardsRef.current[index];
    
    if (card) {
      card.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest',
        inline: 'start'
      });
    }
    
    setCurrentSlide(index);
  }, []);

  const handlePrev = useCallback(() => {
    const newIndex = Math.max(0, currentSlide - 1);
    scrollToSlide(newIndex);
  }, [currentSlide, scrollToSlide]);

  const handleNext = useCallback(() => {
    const newIndex = Math.min(totalSlides - 1, currentSlide + 1);
    scrollToSlide(newIndex);
  }, [currentSlide, totalSlides, scrollToSlide]);

  // Simple scroll tracking - only for dot indicators
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let timeoutId: NodeJS.Timeout;
    
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!container) return;
        
        // Find which card's center is closest to the container's center
        let closestIndex = 0;
        let minDistance = Infinity;
        
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.left + (containerRect.width / 2);
        
        cardsRef.current.forEach((card, index) => {
          if (!card) return;
          
          const cardRect = card.getBoundingClientRect();
          const cardCenter = cardRect.left + (cardRect.width / 2);
          
          // Calculate distance from card center to container center
          const distance = Math.abs(cardCenter - containerCenter);
          
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });
        
        setCurrentSlide(closestIndex);
      }, 100);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [totalSlides]);

  // Simple Intersection Observer for fade-in
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.visible);
        }
      });
    }, { threshold: 0.1 });

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, [contentBlocks.length]);

  // Simple keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNext();
    }
  }, [handlePrev, handleNext]);

  return (
    <>
      {/* SVG Gradients for Icons */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="gradient-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ff9966" />
            <stop offset="50%" stopColor="#ff5e62" />
            <stop offset="100%" stopColor="#ffc371" />
          </linearGradient>
        </defs>
      </svg>

      {/* Section Header */}
      {translations.sectionTitle && (
        <div className={styles.sectionHeader}>
          {translations.badge && (
            <div className={styles.sectionBadge}>{translations.badge}</div>
          )}
          <h2 className={styles.sectionTitle}>{translations.sectionTitle}</h2>
          {translations.sectionIntro && (
            <p className={styles.sectionIntro}>{translations.sectionIntro}</p>
          )}
        </div>
      )}

      {/* SEO: Hidden full content for search engines (after h2 so heading hierarchy is correct) */}
      <div style={{ display: 'none' }} aria-hidden="true">
        {contentBlocks.map((block, index) => (
          <article key={`seo-${index}`}>
            <h3>{block.title}</h3>
            <div>{block.content}</div>
          </article>
        ))}
      </div>

      {/* Carousel Container */}
      <div 
        className={styles.carouselWrapper} 
        onKeyDown={handleKeyDown} 
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label={translations.sectionTitle || "Features"}
      >
        {/* Navigation Buttons - Desktop only */}
        <button
          className={`${styles.navButton} ${styles.navButtonPrev}`}
          onClick={handlePrev}
          disabled={currentSlide === 0}
          aria-label="Previous slide"
          aria-controls="features-carousel"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <button
          className={`${styles.navButton} ${styles.navButtonNext}`}
          onClick={handleNext}
          disabled={currentSlide >= totalSlides - 1}
          aria-label="Next slide"
          aria-controls="features-carousel"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Cards Container */}
        <div 
          ref={scrollContainerRef}
          id="features-carousel"
          className={styles.cardsContainer}
          role="list"
          aria-live="polite"
          aria-atomic="false"
        >
          {contentBlocks.map((block, index) => (
            <article
              key={index}
              ref={(el) => { cardsRef.current[index] = el; }}
              className={`${styles.card} ${styles.scrollReveal}`}
              onClick={() => openModal(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openModal(index);
                }
              }}
              role="listitem"
              tabIndex={0}
              aria-label={`Feature ${index + 1} of ${totalSlides}: ${block.title}. Press Enter to read more`}
              aria-posinset={index + 1}
              aria-setsize={totalSlides}
            >
              {/* Number Badge */}
              <div className={styles.cardBadge} aria-hidden="true">
                {String(index + 1).padStart(2, '0')}
              </div>

              {/* Image */}
              {block.image && (
                <div className={styles.cardImageWrapper}>
                  <Image
                    src={`/images/${imageDirectory}/${block.image}.webp`}
                    alt={altTexts?.kioskFeatures?.[block.image] || block.title}
                    fill
                    sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 400px"
                    style={{ objectFit: 'cover' }}
                    loading="lazy"
                    quality={75}
                  />
                </div>
              )}

              {/* Content */}
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{block.title}</h3>
                <p className={styles.cardPreview}>
                  {block.content.substring(0, 120)}...
                </p>
                <div className={styles.cardCta} aria-hidden="true">
                  <span>{translations.readMore || 'Read more'}</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>

              {/* Glow Effect */}
              <div className={styles.cardGlow} aria-hidden="true" />
            </article>
          ))}
        </div>

        {/* Dot Indicators */}
        <nav className={styles.dotsContainer} aria-label="Carousel pagination">
          {contentBlocks.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${currentSlide === index ? styles.dotActive : ''}`}
              onClick={() => scrollToSlide(index)}
              aria-label={`Go to slide ${index + 1} of ${totalSlides}`}
              aria-current={currentSlide === index ? 'true' : 'false'}
            />
          ))}
        </nav>
      </div>

      {/* Modal */}
      {selectedFeature !== null && (
        <div 
          className={styles.modalOverlay}
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <div 
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className={styles.modalClose}
              onClick={closeModal}
              aria-label="Close modal"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Modal Body */}
            <article className={styles.modalBody}>
              {/* Number Badge */}
              <div className={styles.modalBadge} aria-hidden="true">
                {String(selectedFeature + 1).padStart(2, '0')}
              </div>

              {/* Featured Image */}
              {contentBlocks[selectedFeature].image && (
                <div className={styles.modalImageWrapper}>
                  <Image
                    src={`/images/${imageDirectory}/${contentBlocks[selectedFeature].image}.webp`}
                    alt={altTexts?.kioskFeatures?.[contentBlocks[selectedFeature].image || ''] || contentBlocks[selectedFeature].title}
                    fill
                    sizes="(max-width: 768px) 90vw, 800px"
                    style={{ 
                      objectFit: 'cover',
                      objectPosition: selectedFeature === 1 ? 'left bottom' : selectedFeature === 6 ? 'center top' : 'center center'
                    }}
                    priority
                    quality={85}
                  />
                </div>
              )}

              {/* Text Content */}
              <div className={styles.modalText}>
                <h3 id="modal-title" className={styles.modalTitle}>
                  {contentBlocks[selectedFeature].title}
                </h3>
                <div id="modal-description" className={styles.modalDescription}>
                  {contentBlocks[selectedFeature].content.split('\n\n').map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </div>
      )}
    </>
  );
}
